import { Editor as SlateEditor, Transforms, Element, Range } from 'slate';
import { defaultParagraph } from './paragraph';
import { isPage } from './page';
import { hasText } from 'app/utils/helper-functions';
import { Layout } from 'app/slate/components/Layout';
export const isLayout = (node) => Element.isElement(node) && node.type === 'layout';

export const emptyLayout = (opts) => {
  const horizontal = opts?.horizontal ?? false;
  const withParagraph = opts?.withParagraph ?? true;

  return {
    type: 'layout',
    className: `layout ${horizontal ? 'horizontal' : 'vertical'}`,
    children: withParagraph ? [defaultParagraph()] : [],
  };
};

const layout =
  (options = {}) =>
  (editor) => {
    const markEmpty = options.markEmpty ?? false;
    const { normalizeNode, renderElement, deleteBackward, deleteForward } = editor;

    const normalizePage = (entry) => {
      const [node, path] = entry;
      if (node.children[0]?.type === 'layout') {
        normalizeNode(entry);
        return;
      }

      if (node.children.length) {
        Transforms.wrapNodes(editor, { type: 'layout', children: [] }, { at: [...path, 0] });
      } else {
        Transforms.insertNodes(editor, emptyLayout(), { at: [...path, 0] });
      }
    };

    const normalizeLayout = (entry) => {
      const [node, path] = entry;
      // We're good if the layout already has children
      const hasChildElements = !!node.children?.find((node) => Element.isElement(node));
      const isLayoutLeaf = !node.children?.find((node) => isLayout(node));
      const isEmptyLayoutLeaf = isLayoutLeaf && !hasText(node);

      if (isEmptyLayoutLeaf !== node.isEmptyLayoutLeaf) {
        Transforms.setNodes(editor, { isEmptyLayoutLeaf }, { at: path });
        return;
      }

      if (isLayoutLeaf !== node.isLayoutLeaf) {
        Transforms.setNodes(editor, { isLayoutLeaf }, { at: path });
        return;
      }

      if (hasChildElements) {
        normalizeNode(entry);
        return;
      }

      Transforms.insertNodes(editor, defaultParagraph(), {
        at: [...path, 0],
      });
    };

    editor.normalizeNode = (entry) => {
      const [node] = entry;
      if (isLayout(node)) {
        normalizeLayout(entry);
      } else if (isPage(node)) {
        normalizePage(entry);
      } else {
        normalizeNode(entry);
      }
    };

    editor.renderElement = (props) => {
      if (isLayout(props.element)) {
        return <Layout {...props} markEmpty={markEmpty} />;
      } else {
        return renderElement(props);
      }
    };

    const isInEmptyLayoutLeaf = () => {
      const [currentLayoutLeaf] = Array.from(
        SlateEditor.nodes(editor, {
          match: (n) => isLayout(n) && n.isLayoutLeaf,
          mode: 'lowest',
        })
      );
      return currentLayoutLeaf?.[0]?.isEmptyLayoutLeaf;
    };

    editor.deleteBackward = (unit) => {
      if (!editor.selection) {
        deleteBackward(unit);
        return;
      }

      // For expanded selections, delegate to deleteFragment which handles cross-layout safely
      if (Range.isExpanded(editor.selection)) {
        editor.deleteFragment({ direction: 'backward' });
        return;
      }

      // Never delete from an empty layout box
      if (isInEmptyLayoutLeaf()) {
        return;
      }

      // Default behaviour if in middle of text
      if (editor.selection.focus.offset !== 0) {
        deleteBackward(unit);
        return;
      }

      const before = SlateEditor.before(editor, editor.selection.anchor.path);
      if (!before) {
        deleteBackward(unit);
        return;
      }
      const layoutBefore = Array.from(
        SlateEditor.nodes(editor, {
          at: before,
          match: isLayout,
          mode: 'lowest',
        })
      )[0][0];

      const layoutCurrent = Array.from(
        SlateEditor.nodes(editor, {
          match: isLayout,
          mode: 'lowest',
        })
      )[0][0];

      // Prevent text to be moved between layout boxes with backspace
      if (layoutBefore !== layoutCurrent) {
        return;
      }

      deleteBackward(unit);
    };

    editor.deleteForward = (unit) => {
      if (!editor.selection) {
        deleteForward(unit);
        return;
      }

      // For expanded selections, delegate to deleteFragment which handles cross-layout safely
      if (Range.isExpanded(editor.selection)) {
        editor.deleteFragment({ direction: 'forward' });
        return;
      }

      // Never delete from an empty layout box
      if (isInEmptyLayoutLeaf()) {
        return;
      }

      // Default behaviour if not at end of text
      const { anchor } = editor.selection;
      const [leaf] = SlateEditor.leaf(editor, anchor);
      if (anchor.offset < leaf.text.length) {
        deleteForward(unit);
        return;
      }

      const after = SlateEditor.after(editor, editor.selection.anchor.path);
      if (!after) {
        return;
      }

      const layoutAfter = Array.from(
        SlateEditor.nodes(editor, {
          at: after,
          match: isLayout,
          mode: 'lowest',
        })
      )[0]?.[0];

      const layoutCurrent = Array.from(
        SlateEditor.nodes(editor, {
          match: isLayout,
          mode: 'lowest',
        })
      )[0]?.[0];

      // Prevent text to be moved between layout boxes with forward delete
      if (layoutAfter !== layoutCurrent) {
        return;
      }

      deleteForward(unit);
    };

    return editor;
  };

export default layout;
